"use client";

import { useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { type ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Pencil, Eye, Trash2, Loader2, X, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DataTable,
  DataTableColumnHeader,
  DataTableStatusCell,
  type DataTableConfig,
} from "@admin/_components/data-table";
import { ConfirmDialog } from "@admin/_components/confirm-dialog";
import { useApiList, useApiPost } from "@admin/_lib/api-hooks";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { toast } from "sonner";

// ── Config types ──────────────────────────────────────────────────────────────

export interface ColumnConfig {
  key: string;
  label: string;
  sortable?: boolean;
  type?: "text" | "date" | "datetime" | "status" | "badge" | "number" | "currency";
  badgeMap?: Record<string, string>;
}

export interface PreviewFieldConfig {
  key: string;
  label: string;
  /** text = plain string, html = rendered HTML (admin-safe), image = img tag, date = formatted date, datetime = date + time, badge = badge chip, answers = quiz answer array, videos = nested video entry array */
  type?: "text" | "html" | "image" | "date" | "datetime" | "badge" | "currency" | "answers" | "videos";
}

export interface DateRangeFieldConfig {
  /** Display label, e.g. "Created Date" → rendered as "Created Date From" / "Created Date To" */
  label: string;
  /** DB field name to filter on, e.g. "created_at" */
  field: string;
  /** Optional override for the "from" label; defaults to `${label} From` */
  fromLabel?: string;
  /** Optional override for the "to" label; defaults to `${label} To` */
  toLabel?: string;
}

export interface GenericListConfig {
  title: string;
  subtitle?: string;
  icon?: React.ElementType<{ className?: string }>;

  /** Primary list endpoint */
  listEndpoint: string;
  countEndpoint?: string;

  statusEndpoint?: string;
  deleteEndpoint?: string;
  addPath?: string;
  addButtonLabel?: string;
  editPath?: string;
  viewPath?: string;

  /** When set, adds a preview eye-icon action per row that opens an inline dialog */
  previewEndpoint?: string;
  /** Fields to display in the preview dialog */
  previewFields?: PreviewFieldConfig[];

  columns: ColumnConfig[];
  searchFields?: { label: string; field: string; placeholder?: string }[];
  filterFields?: {
    label: string;
    field: string;
    values: { val: string; name: string }[];
  }[];
  /**
   * When true, use nested pagination format (limit/skip inside condition).
   * Also adds project/token fields.
   */
  nestedPagination?: boolean;
  /** Default sort field. Default: "_id" */
  defaultSortField?: string;
  /** Default sort type. Default: "desc" */
  defaultSortType?: "asc" | "desc";
  /**
   * Payload format for status toggle.
   * 'simple': { _id, status }
   * 'package': { source: 'package', data: { ...row, id: row._id }, secret: 'na', token: '' }
   */
  statusPayloadType?: "simple" | "package";
  /** Date-range filters sent as $gte/$lte against the specified DB field */
  dateRangeFields?: DateRangeFieldConfig[];
  /**
   * When true, preview uses the already-fetched row data instead of making an
   * extra API call. Set `previewFields` to specify which fields to render.
   * `previewEndpoint` is not required when this is set.
   */
  previewFromRow?: boolean;
}

// ── Preview Dialog ─────────────────────────────────────────────────────────────

function PreviewDialog({
  open,
  onClose,
  endpoint,
  recordId,
  fields,
  title,
  staticRecord,
}: {
  open: boolean;
  onClose: () => void;
  endpoint: string;
  recordId: string | null;
  fields: PreviewFieldConfig[];
  title: string;
  /** When provided, skips the API fetch and renders this data directly */
  staticRecord?: Record<string, unknown> | null;
}) {
  const { data: session } = useSession();
  const baseUrl =
    process.env.NEXT_PUBLIC_API_CLOUDFRONT ||
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://api.divineinfinitebeing.com/";

  const { data: fetchedRecord, isLoading } = useQuery({
    queryKey: [endpoint, recordId],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ _id: recordId }),
      });
      const json = await res.json();
      return json.results?.res ?? json.results ?? json;
    },
    enabled: open && !!recordId && !!session?.token && !staticRecord,
  });

  const record = staticRecord ?? fetchedRecord;

  // Resolve dot-notation paths like "ip_info.city" from nested objects
  function resolveValue(obj: unknown, path: string): unknown {
    return path.split(".").reduce<unknown>((acc, key) => {
      if (acc != null && typeof acc === "object") {
        return (acc as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);
  }

  function renderFieldValue(field: PreviewFieldConfig, val: unknown) {
    if (val == null || val === "") {
      return <span className="text-xs text-muted-foreground">—</span>;
    }

    switch (field.type) {
      case "html":
        return (
          <div
            className="prose prose-sm max-w-none dark:prose-invert text-sm leading-relaxed"
            // Admin-controlled HTML content; safe in admin context
            dangerouslySetInnerHTML={{ __html: String(val) }}
          />
        );
      case "image": {
        const src = String(val);
        return src ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={src} alt={field.label} className="max-h-48 rounded-lg object-contain border" style={{ borderColor: "hsl(240 20% 22%)" }} />
        ) : (
          <span className="text-xs text-muted-foreground">No image</span>
        );
      }
      case "date": {
        const d = typeof val === "number" ? new Date(val) : new Date(String(val));
        return (
          <span className="text-sm text-muted-foreground">
            {isNaN(d.getTime()) ? String(val) : format(d, "MMM dd, yyyy")}
          </span>
        );
      }
      case "datetime": {
        const d = typeof val === "number" ? new Date(val) : new Date(String(val));
        return (
          <span className="text-sm text-muted-foreground">
            {isNaN(d.getTime()) ? String(val) : format(d, "MMM dd, yyyy HH:mm")}
          </span>
        );
      }
      case "badge":
        return (
          <Badge
            className="border text-[10px]"
            style={{ background: "rgba(255,168,0,0.1)", color: "#ffa800", borderColor: "rgba(255,168,0,0.3)" }}
          >
            {String(val)}
          </Badge>
        );
      case "answers": {
        const answers = Array.isArray(val) ? val as Array<{ answer?: string; correct_answer?: boolean }> : [];
        if (answers.length === 0) return <span className="text-xs text-muted-foreground">No answers</span>;
        return (
          <div className="space-y-1.5">
            {answers.map((a, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${
                  a.correct_answer
                    ? "bg-green-500/10 border border-green-500/20 text-green-400"
                    : "bg-muted/30"
                }`}
              >
                {a.correct_answer && <CheckCircle2 className="size-3.5 shrink-0" />}
                <span>{a.answer ?? "—"}</span>
                {a.correct_answer && <span className="ml-auto text-xs font-medium">Correct</span>}
              </div>
            ))}
          </div>
        );
      }
      case "videos": {
        const entries = Array.isArray(val)
          ? (val as Array<Record<string, unknown>>)
          : [];
        if (entries.length === 0)
          return <span className="text-xs text-muted-foreground">No video entries</span>;
        return (
          <div className="space-y-1.5">
            {entries.map((v, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm px-3 py-2 rounded-md bg-muted/30"
              >
                <span
                  className="shrink-0 text-xs font-semibold mt-0.5"
                  style={{ color: v.videotype === "1" ? "#ffa800" : "#25aceb" }}
                >
                  {v.videotype === "1" ? "YouTube" : "File"}
                </span>
                <div className="min-w-0 space-y-0.5">
                  {v.videotype === "1" ? (
                    <>
                      {v.youtube_title && (
                        <p className="text-xs font-medium truncate">{String(v.youtube_title)}</p>
                      )}
                      {v.youtube_link && (
                        <p className="text-xs text-muted-foreground truncate">{String(v.youtube_link)}</p>
                      )}
                    </>
                  ) : (
                    <>
                      {v.video_title && (
                        <p className="text-xs font-medium">{String(v.video_title)}</p>
                      )}
                      {v.video_url && (
                        <p className="text-xs text-muted-foreground truncate">{String(v.video_url)}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      }
      default:
        return <span className="text-sm">{String(val)}</span>;
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ borderColor: "hsl(240 20% 22%)" }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            {title} Preview
          </DialogTitle>
        </DialogHeader>

        {isLoading && !staticRecord ? (
          <div className="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : record ? (
          <div className="space-y-4 pt-2">
            {fields.map((field) => (
              <div key={field.key} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {field.label}
                </p>
                <div>{renderFieldValue(field, resolveValue(record, field.key))}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">No data found</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function GenericListPage({ config }: { config: GenericListConfig }) {
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState(config.defaultSortField || "_id");
  const [sortType, setSortType] = useState<"asc" | "desc">(config.defaultSortType || "desc");
  const [searchCondition, setSearchCondition] = useState<Record<string, unknown>>({});
  const [filterCondition, setFilterCondition] = useState<Record<string, unknown>>({});
  const [dateCondition, setDateCondition] = useState<Record<string, unknown>>({});

  // Preview state
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRowData, setPreviewRowData] = useState<Record<string, unknown> | null>(null);

  const { data, isLoading, isFetching, refetch } = useApiList<Record<string, unknown>>(
    config.listEndpoint,
    {
      page,
      limit,
      sortField,
      sortType,
      condition: {},
      searchcondition: { ...searchCondition, ...filterCondition, ...dateCondition },
      countEndpoint: config.countEndpoint,
      nestedPagination: config.nestedPagination,
    }
  );

  const statusMutation = useApiPost(config.statusEndpoint ?? "", {
    invalidateKeys: config.statusEndpoint
      ? [config.listEndpoint, ...(config.countEndpoint ? [config.countEndpoint] : [])]
      : [],
  });
  const deleteMutation = useApiPost(config.deleteEndpoint ?? "", {
    invalidateKeys: config.deleteEndpoint
      ? [config.listEndpoint, ...(config.countEndpoint ? [config.countEndpoint] : [])]
      : [],
  });

  const handleSearch = useCallback((field: string, value: string) => {
    setPage(1);
    setSearchCondition((prev) => {
      if (!value) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: { $regex: value, $options: "i" } };
    });
  }, []);

  const handleFilter = useCallback((field: string, value: string) => {
    setPage(1);
    setFilterCondition((prev) => {
      if (!value) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return {
        ...prev,
        [field]: field === "status" ? (value === "1" ? 1 : 0) : value,
      };
    });
  }, []);

  const handleDateFilter = useCallback((field: string, start?: string, end?: string) => {
    setPage(1);
    setDateCondition((prev) => {
      if (!start && !end) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      const range: Record<string, unknown> = {};
      if (start) {
        range.$gte = new Date(start).getTime();
      }
      if (end) {
        const endDate = new Date(end);
        endDate.setHours(23, 59, 59, 999);
        range.$lte = endDate.getTime();
      }
      return { ...prev, [field]: range };
    });
  }, []);

  const handleSort = useCallback(
    (field: string, type: "asc" | "desc") => {
      setSortField(field);
      setSortType(type);
    },
    []
  );

  // ── Build columns ───────────────────────────────────────────────────────────

  const columns: ColumnDef<Record<string, unknown>>[] = [
    ...config.columns.map((col): ColumnDef<Record<string, unknown>> => {
      if (col.type === "status" && config.statusEndpoint) {
        return {
          accessorKey: col.key,
          header: col.label,
          cell: ({ row }) => (
            <ConfirmDialog
              trigger={
                <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                  <div className="pointer-events-none">
                    <DataTableStatusCell
                      status={row.original[col.key] as number}
                      onToggle={async () => {}} // Disabled — handled in onConfirm
                    />
                  </div>
                </div>
              }
              title="Change Status"
              description={`Are you sure you want to ${
                (row.original[col.key] as number) === 1 ? "deactivate" : "activate"
              } this item?`}
              confirmLabel="Change"
              onConfirm={async () => {
                const newStatus = (row.original[col.key] as number) === 1 ? 0 : 1;
                const _id = row.original._id as string;

                const payload =
                  config.statusPayloadType === "package"
                    ? {
                        source: "package",
                        data: {
                          ...row.original,
                          status: newStatus,
                          id: _id,
                        },
                        secret: "na",
                        token: "",
                      }
                    : {
                        _id,
                        status: newStatus,
                      };

                try {
                  await statusMutation.mutateAsync(payload);
                  toast.success("Status updated successfully");
                  void refetch();
                } catch {
                  toast.error("Failed to update status");
                }
              }}
            />
          ),
        };
      }

      if (col.type === "date" || col.type === "datetime") {
        return {
          accessorKey: col.key,
          header: col.sortable
            ? ({ column }) => (
                <DataTableColumnHeader column={column} title={col.label} />
              )
            : col.label,
          cell: ({ row }) => {
            const val = row.original[col.key] as number | string | undefined;
            if (!val) return <span className="text-xs text-muted-foreground">—</span>;
            const d = typeof val === "number" ? new Date(val) : new Date(val);
            return (
              <span className="text-xs text-muted-foreground">
                {format(d, col.type === "datetime" ? "MMM dd, yyyy HH:mm" : "MMM dd, yyyy")}
              </span>
            );
          },
          enableSorting: col.sortable ?? false,
        };
      }

      if (col.type === "badge" && col.badgeMap) {
        const badgeMap = col.badgeMap;
        return {
          accessorKey: col.key,
          header: col.label,
          cell: ({ row }) => {
            const val = row.original[col.key] as string;
            return (
              <Badge
                className="border text-[10px]"
                style={{
                  background: "rgba(255,168,0,0.1)",
                  color: "#ffa800",
                  borderColor: "rgba(255,168,0,0.3)",
                }}
              >
                {badgeMap[val] ?? val}
              </Badge>
            );
          },
        };
      }

      if (col.type === "currency") {
        return {
          accessorKey: col.key,
          header: col.sortable
            ? ({ column }) => (
                <DataTableColumnHeader column={column} title={col.label} />
              )
            : col.label,
          cell: ({ row }) => {
            const val = row.original[col.key] as number | string | undefined;
            if (val == null || val === "") return "—";
            const n = typeof val === "string" ? parseFloat(val) : val;
            return (
              <span className="text-sm font-semibold" style={{ color: "#16a085" }}>
                {isNaN(n)
                  ? String(val)
                  : new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(n)}
              </span>
            );
          },
          enableSorting: col.sortable ?? false,
        };
      }

      if (col.type === "number") {
        return {
          accessorKey: col.key,
          header: col.sortable
            ? ({ column }) => (
                <DataTableColumnHeader column={column} title={col.label} />
              )
            : col.label,
          cell: ({ row }) => {
            const val = row.original[col.key];
            if (val == null) return <span className="text-xs text-muted-foreground">—</span>;
            return (
              <Badge
                className="border text-[10px]"
                style={{
                  background: "rgba(255,168,0,0.1)",
                  color: "#ffa800",
                  borderColor: "rgba(255,168,0,0.3)",
                }}
              >
                {String(val)}
              </Badge>
            );
          },
          enableSorting: col.sortable ?? false,
        };
      }

      return {
        accessorKey: col.key,
        header: col.sortable
          ? ({ column }) => (
              <DataTableColumnHeader column={column} title={col.label} />
            )
          : col.label,
        cell: ({ row }) => {
          const val = row.original[col.key];
          return val != null ? (
            <span className="text-sm">{String(val)}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
        enableSorting: col.sortable ?? false,
      };
    }),

    // ── Action column ───────────────────────────────────────────────────────
    ...(config.editPath || config.viewPath || config.deleteEndpoint || config.previewEndpoint || config.previewFromRow
      ? [
          {
            id: "actions",
            header: () => (
              <span className="text-xs text-muted-foreground">Actions</span>
            ),
            cell: ({
              row,
            }: {
              row: { original: Record<string, unknown> };
            }) => (
              <div className="flex items-center gap-1">
                {(config.previewEndpoint || config.previewFromRow) && (
                  <button
                    onClick={() => {
                      setPreviewId(row.original._id as string);
                      setPreviewRowData(config.previewFromRow ? row.original : null);
                      setPreviewOpen(true);
                    }}
                    title="Preview"
                    className="inline-flex size-7 items-center justify-center rounded transition-colors hover:bg-[#ffa800]/15"
                    style={{ color: "#ffa800" }}
                  >
                    <Eye className="size-3.5" />
                  </button>
                )}
                {config.editPath && (
                  <button
                    onClick={() =>
                      router.push(
                        `${config.editPath}/${row.original._id as string}`
                      )
                    }
                    title="Edit"
                    className="inline-flex size-7 items-center justify-center rounded transition-colors hover:bg-[#25aceb]/15"
                    style={{ color: "#25aceb" }}
                  >
                    <Pencil className="size-3.5" />
                  </button>
                )}
                {config.viewPath && (
                  <button
                    onClick={() =>
                      router.push(
                        `${config.viewPath}/${row.original._id as string}`
                      )
                    }
                    title="View"
                    className="inline-flex size-7 items-center justify-center rounded transition-colors hover:bg-[#16a085]/15"
                    style={{ color: "#16a085" }}
                  >
                    <Eye className="size-3.5" />
                  </button>
                )}
                {config.deleteEndpoint && (
                  <ConfirmDialog
                    trigger={
                      <button
                        title="Delete"
                        className="inline-flex size-7 items-center justify-center rounded transition-colors hover:bg-[#e94a49]/15"
                        style={{ color: "#e94a49" }}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    }
                    title="Delete Item"
                    description="Are you sure? This action cannot be undone."
                    confirmLabel="Delete"
                    variant="destructive"
                    onConfirm={async () => {
                      try {
                        await deleteMutation.mutateAsync({
                          _id: row.original._id as string,
                        });
                        toast.success("Deleted successfully");
                        void refetch();
                      } catch {
                        toast.error("Failed to delete");
                      }
                    }}
                  />
                )}
              </div>
            ),
          } as ColumnDef<Record<string, unknown>>,
        ]
      : []),
  ];

  // ── Table config ────────────────────────────────────────────────────────────

  const tableConfig: DataTableConfig = {
    searchFields: config.searchFields?.map((s) => ({
      label: s.label,
      field: s.field,
      placeholder: s.placeholder ?? `Search ${s.label}...`,
    })),
    filterFields: config.filterFields,
    dateFields: config.dateRangeFields?.map((d) => ({
      startLabel: d.fromLabel ?? `${d.label} From`,
      endLabel: d.toLabel ?? `${d.label} To`,
      field: d.field,
    })),
    customButtons: config.addPath
      ? [
          {
            label: config.addButtonLabel ?? `+ Add`,
            onClick: () => router.push(config.addPath!),
          },
        ]
      : [],
  };

  const Icon = config.icon;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div
          className="h-7 w-1 rounded-full shrink-0"
          style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }}
        />
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            {Icon && <Icon className="size-4" />}
            {config.title}
          </h1>
          {config.subtitle && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {config.subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Table card */}
      <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
        <CardHeader
          className="border-b py-3 px-5"
          style={{ borderColor: "hsl(240 20% 20%)" }}
        >
          <CardTitle className="flex items-center gap-2 text-base">
            <span
              className="h-4 w-0.5 rounded-full"
              style={{ background: "#ffa800" }}
            />
            {config.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 relative">
          {(isFetching || statusMutation.isPending || deleteMutation.isPending) && !isLoading && (
            <div className="absolute top-2 right-4 z-10">
              <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Global Action Loader Overlay */}
          {(statusMutation.isPending || deleteMutation.isPending) && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-xl animate-in fade-in duration-200">
              <div className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-card border shadow-2xl scale-110">
                <Loader2 className="size-8 animate-spin text-[#ffa800]" />
                <p className="text-sm font-medium text-foreground">Processing...</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading...
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={data?.data ?? []}
              totalCount={data?.count ?? 0}
              config={tableConfig}
              page={page}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
              onSearch={handleSearch}
              onSort={handleSort}
              onFilter={handleFilter}
              onDateFilter={handleDateFilter}
            />
          )}
        </CardContent>
      </Card>

      {/* Preview dialog */}
      {(config.previewEndpoint || config.previewFromRow) && config.previewFields && (
        <PreviewDialog
          open={previewOpen}
          onClose={() => { setPreviewOpen(false); setPreviewId(null); setPreviewRowData(null); }}
          endpoint={config.previewEndpoint ?? ""}
          recordId={previewId}
          fields={config.previewFields}
          title={config.title}
          staticRecord={previewRowData}
        />
      )}
    </div>
  );
}
